import { Component, ComponentInterface, Element, h, Host, JSX, Prop } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { isSlotted } from '../../utils/is-slotted';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';

export type PriceSizeVariant = 's' | 'm' | 'l';

/**
 * @deprecated This pattern is deprecated and will be removed in the next major version. Please, use [PLS.UI](https://pages.git.i.mercedes-benz.com/dh-io-pls/dcps-product-pricing/) instead.
 */
/**
 * @slot headline - Headline for the price-component
 * @slot start-from - Label for starting from as in "from: 300,00 €"
 * @slot price - Price value of the component
 * @slot strike-price - Optional strike price below the value
 * @slot additional-text - Additional text below the value
 */
@Component({
  tag: 'wb-price',
  styleUrl: './price.scss',
  shadow: true,
})
export class PriceComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** the color scheme */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** size variant of the price */
  @Prop({ reflect: true })
  public size: PriceSizeVariant = 'm';

  /** headline for the component */
  @Prop({ reflect: true })
  public headline?: string;

  /** label for starting from as in "from: 300,00 €" */
  @Prop({ reflect: true })
  public startFrom?: string;

  /** price value of the component */
  @Prop({ reflect: true })
  public price?: string;

  /** optional strike price below the value */
  @Prop({ reflect: true })
  public strikePrice?: string;

  /** additional text below the value */
  @Prop({ reflect: true })
  public additionalText?: string;

  /** optional tooltip text content */
  @Prop({ reflect: true })
  public tooltip?: string;

  private TagNames = transformTagNames(this, 'wb-price', ['wb-text', 'wb-icon', 'wb-tooltip']);

  public render(): JSX.Element {
    const { WbText, WbIcon, WbTooltip } = this.TagNames;
    const headlineAndAdditionalTextSize = this.size === 's' ? 'm' : 'l';
    const hasStartFrom = this.startFrom || isSlotted(this.el, 'start-from');
    const hasStrikePrice = this.strikePrice || isSlotted(this.el, 'strike-price');

    return (
      <Host class="wb-price" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <WbText class="headline" size={headlineAndAdditionalTextSize} static>
          {this.headline || <slot name="headline" />}
        </WbText>
        <div class="value">
          {hasStartFrom && (
            <WbText class="start-from" tag="span" size="l">
              {this.startFrom || <slot name="start-from" />}
            </WbText>
          )}
          <span class="price">{this.price || <slot name="price" />}</span>
          {this.tooltip && (
            <WbTooltip>
              <WbIcon name="bds/info-circle/24" />
              <div slot="content">{this.tooltip}</div>
            </WbTooltip>
          )}
        </div>
        {hasStrikePrice && (
          <div class="strike-price">
            <del aria-hidden="true">{this.strikePrice || <slot name="strike-price" />}</del>
          </div>
        )}
        <WbText class="additional-text" size={headlineAndAdditionalTextSize} static>
          {this.additionalText || <slot name="additional-text" />}
        </WbText>
      </Host>
    );
  }
}
